export const DEFAULT_AUDIO_SAMPLE_RATE = Number(process.env.REACT_APP_AUDIO_SAMPLE_RATE || 16000);
export const DEFAULT_AUDIO_FRAME_MS = Number(process.env.REACT_APP_AUDIO_FRAME_MS || 20);

export const computeFrameBytes = (sampleRate, frameMs) => {
  return Math.floor((sampleRate * frameMs) / 1000) * 2;
};

const clampSample = (value) => {
  if (value > 1) return 1;
  if (value < -1) return -1;
  return value;
};

export const float32ToPcm16 = (samples) => {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = clampSample(samples[i]);
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

export class CanonicalFrameStreamer {
  constructor({ sampleRate, frameMs, onFrame }) {
    this.sampleRate = sampleRate;
    this.frameMs = frameMs;
    this.samplesPerFrame = Math.floor((sampleRate * frameMs) / 1000);
    this.onFrame = onFrame;

    this.pending = [];
    this.resamplePosition = 0;
    this.lastInputSample = 0;
  }

  getFrameBytes() {
    return this.samplesPerFrame * 2;
  }

  push(floatChunk, inputSampleRate) {
    if (!floatChunk || floatChunk.length === 0) return;

    const normalized = inputSampleRate === this.sampleRate
      ? floatChunk
      : this.resampleChunk(floatChunk, inputSampleRate, this.sampleRate);

    for (let i = 0; i < normalized.length; i += 1) {
      this.pending.push(normalized[i]);
    }

    while (this.pending.length >= this.samplesPerFrame) {
      const frameFloat = this.pending.splice(0, this.samplesPerFrame);
      const pcm16 = float32ToPcm16(frameFloat);
      this.onFrame(pcm16.buffer);
    }
  }

  reset() {
    this.pending = [];
    this.resamplePosition = 0;
    this.lastInputSample = 0;
  }

  resampleChunk(inputChunk, fromRate, toRate) {
    const ratio = fromRate / toRate;
    const output = [];

    let index = this.resamplePosition;

    while (index < inputChunk.length) {
      const leftIndex = Math.floor(index);
      const rightIndex = leftIndex + 1;
      const left = leftIndex >= 0 ? inputChunk[leftIndex] : this.lastInputSample;
      const right = rightIndex < inputChunk.length ? inputChunk[rightIndex] : inputChunk[inputChunk.length - 1];
      const frac = index - leftIndex;
      output.push(left + (right - left) * frac);
      index += ratio;
    }

    this.resamplePosition = index - inputChunk.length;
    this.lastInputSample = inputChunk[inputChunk.length - 1] || 0;

    return output;
  }
}

export class MicrophoneSource {
  constructor(targetSampleRate) {
    this.targetSampleRate = targetSampleRate;
    this.mediaStream = null;
    this.audioContext = null;
    this.mediaSource = null;
    this.workletNode = null;
    this.silentGain = null;
    this.onChunk = null;
  }

  async prepare() {
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: this.targetSampleRate,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    return {
      sampleRate: this.targetSampleRate,
      mode: 'microphone',
    };
  }

  async start(onChunk) {
    this.onChunk = onChunk;
    this.audioContext = new AudioContext({ sampleRate: this.targetSampleRate });
    await this.audioContext.audioWorklet.addModule('/worklets/pcm-capture-worklet.js');

    this.mediaSource = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture-worklet');
    this.silentGain = this.audioContext.createGain();
    this.silentGain.gain.value = 0;

    this.workletNode.port.onmessage = (event) => {
      if (!this.onChunk) return;
      const floatChunk = new Float32Array(event.data);
      this.onChunk(floatChunk, this.audioContext.sampleRate);
    };

    this.mediaSource.connect(this.workletNode);
    this.workletNode.connect(this.silentGain);
    this.silentGain.connect(this.audioContext.destination);

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  async stop() {
    this.onChunk = null;

    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.mediaSource) {
      this.mediaSource.disconnect();
      this.mediaSource = null;
    }

    if (this.silentGain) {
      this.silentGain.disconnect();
      this.silentGain = null;
    }

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }
}

export class AudioFileSource {
  constructor(file, targetSampleRate, frameMs) {
    this.file = file;
    this.targetSampleRate = targetSampleRate;
    this.frameMs = frameMs;
    this.samplesPerFrame = Math.floor((targetSampleRate * frameMs) / 1000);

    this.signal = null;
    this.isStopped = false;
  }

  async prepare() {
    const arrayBuffer = await this.file.arrayBuffer();
    const decodeContext = new AudioContext();
    let decoded;

    try {
      decoded = await decodeContext.decodeAudioData(arrayBuffer.slice(0));
    } finally {
      await decodeContext.close();
    }

    const mono = this.mixToMono(decoded);
    const targetLength = Math.ceil((mono.length * this.targetSampleRate) / decoded.sampleRate);

    const offlineContext = new OfflineAudioContext(1, targetLength, this.targetSampleRate);
    const monoBuffer = offlineContext.createBuffer(1, mono.length, decoded.sampleRate);
    monoBuffer.copyToChannel(mono, 0);

    const source = offlineContext.createBufferSource();
    source.buffer = monoBuffer;
    source.connect(offlineContext.destination);
    source.start(0);

    const rendered = await offlineContext.startRendering();
    this.signal = rendered.getChannelData(0).slice();

    return {
      sampleRate: this.targetSampleRate,
      totalSamples: this.signal.length,
      totalFrames: Math.ceil(this.signal.length / this.samplesPerFrame),
      mode: 'file',
    };
  }

  async start(onChunk, onProgress, fastMode = false) {
    if (!this.signal) {
      throw new Error('Audio file not prepared');
    }

    this.isStopped = false;
    let offset = 0;
    const total = this.signal.length;

    while (offset < total && !this.isStopped) {
      const end = Math.min(offset + this.samplesPerFrame, total);
      const chunk = this.signal.slice(offset, end);

      onChunk(chunk, this.targetSampleRate);
      offset = end;
      onProgress(offset / total);

      if (!fastMode) {
        await new Promise((resolve) => setTimeout(resolve, this.frameMs));
      } else if (offset % (this.samplesPerFrame * 8) === 0) {
        await Promise.resolve();
      }
    }

    return !this.isStopped;
  }

  async stop() {
    this.isStopped = true;
  }

  mixToMono(audioBuffer) {
    const channels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;

    if (channels === 1) {
      return audioBuffer.getChannelData(0).slice();
    }

    const mono = new Float32Array(length);
    for (let c = 0; c < channels; c += 1) {
      const channelData = audioBuffer.getChannelData(c);
      for (let i = 0; i < length; i += 1) {
        mono[i] += channelData[i] / channels;
      }
    }

    return mono;
  }
}
